import { z } from 'zod';
import type { Role } from '../permissions/types.js';
import { failure, success, type Result } from '../types.js';
import { isNotificationEvent, notificationCatalog, type NotificationEvent } from './catalog.js';
import { notificationChannels, notificationGroupChannels } from './policy.js';

/**
 * My preferences › Notifications (SET1, K24, APP-092): which notices a person can receive, and
 * what each channel does for them now. Pure. Preferences never confer authority: a row says what
 * would reach this person if they're a recipient; who is a recipient is decided per notice.
 */

/** Everyone is an employee; the rest are the roles a notice reaches (the union of roles blends). */
export type NotificationReach = Role | 'everyone';

/**
 * Who each event can reach, as roles. `null`: not a personal choice (the go-live welcome is sent
 * once, at launch, by Go live's own switch). The first role a person holds is the row's tag.
 */
export const notificationReach: Readonly<
  Record<NotificationEvent, readonly NotificationReach[] | null>
> = {
  // Spot (APP-049, APP-102): any employee nominates; the committee and supervisors act on them.
  spot_nomination_received: ['everyone'],
  // APP-225: retired; the nominator is told it was received, never how it ended.
  spot_nomination_decided: null,
  // APP-226: the committee and the nominee's manager hear of a cancel.
  spot_nomination_cancelled: ['spot_reviewer', 'manager'],
  // APP-227: the manager whose approval or veto someone recorded for them.
  spot_supervisor_decision_recorded: ['manager'],
  spot_committee_action_needed: ['spot_reviewer'],
  spot_supervisor_action_needed: ['manager'],
  spot_funding_action_needed: ['executive'],
  spot_funding_decided: ['spot_reviewer', 'manager'],
  spot_pool_over_budget: ['executive'],
  spot_fulfillment_action_needed: ['spot_reviewer'],
  spot_statement_check_needed: ['spot_reviewer'],
  // An employee's own award.
  election_opened: ['everyone'],
  award_announced: ['everyone'],
  election_reminder: ['everyone'],
  election_received: ['everyone'],
  // Letters: the default holder can be the requester, the manager or the employee.
  letters_released: ['everyone'],
  // APP-245: an employee's own award letter, sent by whoever holds it.
  letter_sent: ['everyone'],
  // APP-245 D2: whoever holds a Spot letter still to hand out (any person can hold one).
  letter_handout_reminder: ['everyone'],
  role_changed: ['everyone'],
  getting_started: ['everyone'],
  go_live_welcome: null,
  // Leaders who enter awards.
  delegate_submitted: ['sector_lead', 'executive'],
  delegate_approval_needed: ['sector_lead', 'executive'],
  delegate_decision: ['delegate', 'executive_delegate'],
  executive_sector_award: ['sector_lead'],
  overage_decision: ['sector_lead', 'delegate', 'executive_delegate'],
  executive_decision: ['sector_lead', 'delegate', 'executive_delegate', 'executive', 'requester'],
  check_returned: ['sector_lead', 'delegate', 'executive_delegate', 'executive', 'requester'],
  requester_confirmation: ['executive', 'executive_delegate', 'requester'],
  manager_nudge: ['manager'],
  // Executives.
  overage_approval_needed: ['executive'],
  paa_taa_approval_needed: ['executive'],
  self_recognition_approval_needed: ['executive'],
  executive_award_created: ['executive'],
  executive_award_final_summary: ['executive'],
  executive_approval_handled: ['executive'],
  // The Stock Admin team and Payroll.
  stock_admin_queue: ['stock_admin'],
  letter_gap: ['stock_admin'],
  stock_admin_digest: ['stock_admin'],
  election_past_deadline: ['stock_admin'],
  earning_statement_attached: ['stock_admin'],
  shareworks_ready: ['stock_admin'],
  payroll_ready: ['payroll'],
  letter_template_ready: ['stock_admin'],
  // APP-138 §D3: anyone can send feedback, so anyone can hear back about it.
  feedback_replied: ['everyone'],
  feedback_status_changed: ['everyone'],
  // APP-148 D18: a Stock Admin acts for an Executive or a Sector Lead; the team hears a question,
  // and a settlement reaches whoever entered a returned award too.
  acted_for_you: ['executive', 'sector_lead'],
  acted_for_questioned: ['stock_admin', 'system_admin'],
  acted_for_settled: [
    'executive',
    'sector_lead',
    'delegate',
    'requester',
    'stock_admin',
    'system_admin',
  ],
};

/**
 * Why a person can't change a channel:
 * - `always`: sent every time (the Sector Lead's delegate emails; a `bellAlways` notice's bell);
 * - `required`: the organization requires the email (K24);
 * - `group`: the one Executive group email, set by the organization (APP-031);
 * - `digest`: it arrives only in the Stock Admin daily digest (M5's digest mode);
 * - `organization_off`: the organization has it off for everyone;
 * - `not_offered`: this notice never uses the channel;
 * - `with_in_app`: a Teams post comes with the in-app notice, which the person has off.
 */
export type ChannelLock =
  'always' | 'required' | 'group' | 'digest' | 'organization_off' | 'not_offered' | 'with_in_app';

export interface PreferenceChannel {
  /** Whether it reaches the person now, through this channel. */
  readonly on: boolean;
  /** Null: the person's own choice. */
  readonly lock: ChannelLock | null;
}

export interface NotificationPreferenceRow {
  readonly event: NotificationEvent;
  /** The role the row is for: the first of the event's reach the person holds. */
  readonly reach: NotificationReach;
  readonly email: PreferenceChannel;
  readonly inApp: PreferenceChannel;
  /** Null: this notice isn't posted in Teams (or Teams isn't set up here). */
  readonly teams: PreferenceChannel | null;
}

const channels = z.object({ email: z.boolean(), inApp: z.boolean() }).strict();
const eventMap = z
  .record(z.string(), channels)
  .refine((values) => Object.keys(values).every(isNotificationEvent));
/** A person's stored choices: Teams is optional, and on unless they turned it off (AI3). */
const choiceMap = z
  .record(
    z.string(),
    z.object({ email: z.boolean(), inApp: z.boolean(), teams: z.boolean().optional() }).strict(),
  )
  .refine((values) => Object.keys(values).every(isNotificationEvent));
/**
 * Every role a person can hold, so a person's roles always parse: a role missing here refused the
 * whole page (Support's, APP-104). The Record makes a new Role a type error until it's listed.
 * Holding a role adds only the rows `notificationReach` gives it; Support has none (APP-104 D19).
 */
const ROLES: Record<Role, true> = {
  employee: true,
  manager: true,
  sector_lead: true,
  delegate: true,
  executive_delegate: true,
  requester: true,
  executive: true,
  stock_admin: true,
  payroll: true,
  finance: true,
  system_admin: true,
  paa_taa_approver: true,
  spot_reviewer: true,
  support: true,
};
const role = z.enum(Object.keys(ROLES) as [Role, ...Role[]]);
const organizationSchema = z
  .object({
    organization: eventMap.optional(),
    requiredEmails: z.array(z.string()).optional(),
    queueEmailMode: z.enum(['per_request', 'digest']).optional(),
    digestEnabled: z.boolean().optional(),
    managerNudgeEnabled: z.boolean().optional(),
  })
  .strict();
const rowsSchema = organizationSchema
  .extend({
    roles: z.array(role),
    personal: choiceMap.optional(),
    /** The notices posted in Teams when the Teams app is set up here; absent when it isn't. */
    teamsEvents: z.array(z.string()).optional(),
  })
  .strict();

type OrganizationPolicy = z.infer<typeof organizationSchema>;
export interface NotificationChoice {
  readonly email: boolean;
  readonly inApp: boolean;
  readonly teams?: boolean | undefined;
}
type Choices = Readonly<Record<string, NotificationChoice>>;

/** The email and in-app choices only, as the channel policy reads them. */
function emailAndInApp(personal: Choices | undefined) {
  return personal === undefined
    ? undefined
    : Object.fromEntries(
        Object.entries(personal).map(([key, c]) => [key, { email: c.email, inApp: c.inApp }]),
      );
}

function reachOf(event: NotificationEvent, roles: readonly Role[]): NotificationReach | null {
  const reach = notificationReach[event];
  if (reach === null) return null;
  return reach.find((r) => r === 'everyone' || roles.includes(r)) ?? null;
}

/**
 * The person's own preference for one channel: what they saved, else the notice's starting
 * choice (APP-138 §D3: a feedback status change starts with email and Teams off), else on. Every
 * reader of a person's channel uses it: these rows, a change, and the Teams post.
 */
export function chosenChannel(
  personal: Readonly<Record<string, Partial<NotificationChoice>>> | undefined,
  event: NotificationEvent,
  channel: 'email' | 'inApp' | 'teams',
): boolean {
  const saved = personal?.[event]?.[channel];
  if (saved !== undefined) return saved;
  return channel === 'inApp'
    ? true
    : (notificationCatalog[event].personalDefault?.[channel] ?? true);
}

function rowFor(
  event: NotificationEvent,
  reach: NotificationReach,
  policy: OrganizationPolicy,
  choices: Choices | undefined,
  teamsEvents: readonly string[] | undefined,
): NotificationPreferenceRow {
  const personal = emailAndInApp(choices);
  const definition = notificationCatalog[event];
  const base = {
    event,
    organization: policy.organization,
    requiredEmails: policy.requiredEmails,
    queueEmailMode: policy.queueEmailMode,
    digestEnabled: policy.digestEnabled,
    managerNudgeEnabled: policy.managerNudgeEnabled,
  };
  // What the organization sends to someone with every channel on (so a starting choice of off
  // never reads as "off for everyone"), and with the person's own choices.
  const everyone = notificationChannels({
    ...base,
    personal: { [event]: { email: true, inApp: true } },
  });
  const mine = notificationChannels({ ...base, personal });
  if (!everyone.ok || !mine.ok) throw new Error(`notification policy for ${event}`);
  // The digest is an email only; getting started (K25) and feedback notices (APP-138 §D3) are
  // always in the bell.
  const inApp: PreferenceChannel = definition.bellAlways
    ? { on: true, lock: 'always' }
    : event === 'stock_admin_digest'
      ? { on: false, lock: 'not_offered' }
      : !everyone.value.inApp
        ? { on: false, lock: 'organization_off' }
        : { on: chosenChannel(personal, event, 'inApp'), lock: null };
  const digestActive = policy.digestEnabled === true || policy.queueEmailMode === 'digest';
  const executiveGroup =
    reach === 'executive' &&
    definition.audiences.includes('executives') &&
    event !== 'self_recognition_approval_needed';
  // Staff notices the daily digest carries instead of an email of their own (APP-031):
  // Shareworks rows, and the queue in M5's digest mode.
  const inDigest =
    event !== 'stock_admin_digest' &&
    (definition.emailDelivery === 'digest' ||
      (event === 'stock_admin_queue' && policy.queueEmailMode === 'digest'));
  let email: PreferenceChannel;
  if (!definition.emailSupported) email = { on: false, lock: 'not_offered' };
  else if (executiveGroup) {
    const group = notificationGroupChannels({
      event,
      organization: policy.organization,
      requiredEmails: policy.requiredEmails,
    });
    email = { on: group.ok && group.value.email, lock: 'group' };
  } else if (definition.alwaysEmail) email = { on: true, lock: 'always' };
  else if (inDigest) {
    const digest = notificationChannels({
      ...base,
      event: 'stock_admin_digest',
      personal,
    });
    email =
      digestActive && digest.ok
        ? { on: digest.value.email, lock: 'digest' }
        : { on: false, lock: 'organization_off' };
  } else if (!everyone.value.email) email = { on: false, lock: 'organization_off' };
  else if (everyone.value.emailRequired) email = { on: true, lock: 'required' };
  else email = { on: mine.value.email, lock: null };
  // Teams (AI3, APP-089): the post comes with the in-app notice, for someone who has the app.
  const teams: PreferenceChannel | null =
    teamsEvents?.includes(event) !== true
      ? null
      : inApp.on
        ? { on: chosenChannel(choices, event, 'teams'), lock: null }
        : { on: false, lock: 'with_in_app' };
  return { event, reach, email, inApp, teams };
}

/**
 * The person's rows, in catalog order: every event that can reach one of their roles, with what
 * each channel does for them now. A required email shows on and locked (K24); the Executive group
 * email is the organization's (no ineffective personal switch, APP-031).
 */
export function notificationPreferenceRows(
  input: unknown,
): Result<readonly NotificationPreferenceRow[]> {
  const parsed = rowsSchema.safeParse(input);
  if (!parsed.success) return failure('invalid_notification_preferences');
  const { roles, personal, teamsEvents, ...policy } = parsed.data;
  const rows: NotificationPreferenceRow[] = [];
  for (const key of Object.keys(notificationCatalog)) {
    if (!isNotificationEvent(key)) continue;
    const reach = reachOf(key, roles);
    if (reach === null) continue;
    rows.push(rowFor(key, reach, policy, personal, teamsEvents));
  }
  return success(rows);
}

const changesSchema = organizationSchema
  .extend({
    roles: z.array(role),
    current: choiceMap.optional(),
    teamsEvents: z.array(z.string()).optional(),
    changes: z.record(
      z.string(),
      z
        .object({
          email: z.boolean().optional(),
          inApp: z.boolean().optional(),
          teams: z.boolean().optional(),
        })
        .strict()
        .refine((c) => c.email !== undefined || c.inApp !== undefined || c.teams !== undefined),
    ),
  })
  .strict();

/**
 * A person's notification choices after a change, or why it can't be made. Only the person's own
 * rows can change, and only a channel that is theirs to choose: a required, always-on or group
 * email, or a notice the organization has off, can't be switched by a person (K24). Setting a
 * channel to what it already is changes nothing and is allowed.
 */
export function applyNotificationChoices(
  input: unknown,
): Result<Record<string, NotificationChoice>> {
  const parsed = changesSchema.safeParse(input);
  if (!parsed.success) return failure('invalid_notification_choices');
  const { roles, current, changes, teamsEvents, ...policy } = parsed.data;
  const next: Record<string, NotificationChoice> = { ...(current ?? {}) };
  for (const [key, change] of Object.entries(changes)) {
    if (!isNotificationEvent(key)) return failure('unknown_notification_event', key);
    const reach = reachOf(key, roles);
    if (reach === null) return failure('notification_not_yours', key);
    const stored: { email: boolean; inApp: boolean; teams?: boolean | undefined } = {
      email: chosenChannel(current, key, 'email'),
      inApp: chosenChannel(current, key, 'inApp'),
      ...(current?.[key]?.teams === undefined ? {} : { teams: current[key].teams }),
    };
    // Email and in app first: a Teams post follows the in-app choice made in the same change.
    for (const channel of ['email', 'inApp', 'teams'] as const) {
      const wanted = change[channel];
      const now = chosenChannel({ [key]: stored }, key, channel);
      if (wanted === undefined || wanted === now) continue;
      const row = rowFor(key, reach, policy, { ...current, [key]: stored }, teamsEvents);
      const state = channel === 'teams' ? row.teams : row[channel];
      if (state === null || state.lock !== null) return failure('notification_choice_locked', key);
      stored[channel] = wanted;
    }
    next[key] = stored;
  }
  return success(next);
}
